export const PT = 25.4 / 72;
export const uid = () => Math.random().toString(36).slice(2, 9);

export function newLine(cfg, text = "") {
  const base = cfg.default_lines?.[cfg.default_lines.length - 1] || {};
  return {
    id: uid(), text, font: base.font || cfg.default_font || "arimo",
    size_pt: Math.max(cfg.min_font_pt, Math.min(cfg.max_font_pt, base.curve ? 8 : base.size_pt || 8)),
    bold: false, italic: false, underline: false, align: "center", curve: null, offset_x: 0, offset_y: 0, letter_spacing_mm: 0,
  };
}

export function initialDesign(product) {
  return {
    lines: (product.config.default_lines || []).map((l) => ({ ...l, id: uid(), pristine: true })),
    graphics: [],
    border: { style: "none", width_pt: 1 },
    line_spacing: 1.15,
    autofit: true,
    block_x: 0,
    block_y: 0,
  };
}

export function borderInset(border) {
  if (!border || border.style === "none") return 0;
  const bw = (border.width_pt || 1) * PT;
  return 0.3 + bw + (border.style === "double" ? bw + 0.7 : 0);
}

const pickText = (l) => ({
  text: l.text, font: l.font, size_pt: l.size_pt, bold: !!l.bold, italic: !!l.italic, underline: !!l.underline,
  align: l.align, curve: l.curve || null, letter_spacing_mm: l.letter_spacing_mm || 0,
});

let _ctx = null;
export function measureMm(l, sizePt) {
  if (typeof document === "undefined") return 0;
  _ctx = _ctx || document.createElement("canvas").getContext("2d");
  _ctx.font = `${l.italic ? "italic " : ""}${l.bold ? 700 : 400} 100px "hs-${l.font}"`;
  if ("fontKerning" in _ctx) _ctx.fontKerning = "none";
  const s = sizePt * PT;
  return (_ctx.measureText(l.text || "").width / 100) * s + Math.max(0, (l.text || "").length - 1) * (l.letter_spacing_mm || 0);
}

const floor25 = (v) => Math.floor(v * 4) / 4;

export function computeLayout(product, design) {
  const W = product.width_mm, H = product.height_mm, cfg = product.config;
  const m = (cfg.margin_mm ?? 1) + borderInset(design.border);
  const ls = design.line_spacing || 1.15;
  const bx = design.block_x || 0;
  const round = product.shape === "round" || product.shape === "oval";
  const autofit = design.autofit !== false;
  const els = [];
  let a0 = m, a1 = W - m;
  if (!round)
    design.graphics.forEach((g) => {
      if (g.x + g.w / 2 < W / 2) a0 = Math.max(a0, g.x + g.w + 0.8);
      else a1 = Math.min(a1, g.x - 0.8);
    });
  if (a1 - a0 < 5) { a0 = m; a1 = W - m; }
  const straight = design.lines.map((l, i) => ({ l, i, size: l.size_pt })).filter((o) => !o.l.curve);
  if (autofit) {
    const avail = a1 - a0;
    straight.forEach((o) => {
      if (o.l.manual) return;
      const w = measureMm(o.l, o.size);
      if (w > avail) o.size = Math.max(cfg.min_font_pt, floor25((o.size * avail) / w));
    });
    const availH = round ? Math.min(W, H) * 0.6 : H - 2 * m;
    const h = (arr) => arr.reduce((a, o) => a + o.size * PT * ls, 0);
    const auto = straight.filter((o) => !o.l.manual), room = availH - h(straight.filter((o) => o.l.manual)), tot = h(auto);
    if (tot > room) auto.forEach((o) => { o.size = Math.max(cfg.min_font_pt, floor25((o.size * Math.max(0, room)) / tot)); });
  }
  const total = straight.reduce((a, o) => a + o.size * PT * ls, 0);
  let acc = (H - total) / 2 + (design.block_y || 0);
  const cx = (a0 + a1) / 2 + bx;
  straight.forEach(({ l, i, size }) => {
    const s = size * PT;
    const y = acc + (s * ls) / 2 + s * 0.35 + (l.offset_y || 0);
    acc += s * ls;
    const x = (l.align === "left" ? a0 + bx : l.align === "right" ? a1 + bx : cx) + (l.offset_x || 0);
    els.push({ type: "text", ref: { kind: "line", i }, ...pickText(l), size_pt: size, fitted: size < l.size_pt, x, y });
  });
  const R = Math.min(W, H) / 2;
  design.lines.forEach((l, i) => {
    if (!l.curve) return;
    const s = l.size_pt * PT;
    const r = l.curve === "top" ? R - m - s * 0.72 - (l.offset_y || 0) : R - m - s * 0.22 + (l.offset_y || 0);
    els.push({ type: "text", ref: { kind: "line", i }, ...pickText(l), cx: W / 2, cy: H / 2, r });
  });
  design.graphics.forEach((g, i) => {
    const ref = { kind: "graphic", i };
    if (g.type === "qr") els.push({ type: "qr", ref, data: g.data, x: g.x, y: g.y, size: g.w });
    else if (g.type === "clipart") els.push({ type: "clipart", ref, name: g.name, x: g.x, y: g.y, w: g.w, h: g.h });
    else els.push({ type: "image", ref, src: g.src, ctype: g.ctype, x: g.x, y: g.y, w: g.w, h: g.h });
  });
  return { width_mm: W, height_mm: H, shape: product.shape, border: design.border, elements: els };
}

export function basePrice(product, design) {
  const cfg = product.config;
  const extra = Math.max(0, design.lines.length - (cfg.default_lines?.length || 0)) * (cfg.extra_line_price || 0);
  const logo = design.graphics.some((g) => g.type === "image") ? cfg.logo_price || 0 : 0;
  return product.price + extra + logo;
}

export function tierDiscount(tiers, qty) {
  return (tiers || []).reduce((p, t) => (qty >= t.min_qty ? Math.max(p, t.discount_pct) : p), 0);
}

export function discounted(base, tiers, qty) {
  return Math.round(base * (1 - tierDiscount(tiers, qty) / 100) * 100) / 100;
}

export function unitPrice(product, design, qty = 1) {
  return discounted(basePrice(product, design), product.config.qty_tiers, qty);
}

export function maxLines(cfg) {
  return cfg.customer_can_add_lines ? cfg.max_lines : cfg.default_lines?.length || 0;
}

export const recenter = (d) => ({ ...d, block_x: 0, block_y: 0, lines: d.lines.map((l) => ({ ...l, offset_x: 0, offset_y: 0 })) });

export const centerAll = (d) => ({ ...recenter(d), line_spacing: 1.15, lines: recenter(d).lines.map((l) => (l.curve ? l : { ...l, align: "center" })) });

export const isOffCenter = (d) => !!(d.block_x || d.block_y || d.lines.some((l) => l.offset_x || l.offset_y));

export const unfilledLines = (d) => d.lines.map((l, i) => ({ l, i })).filter(({ l }) => l.pristine || !l.text.trim());

export const cleanDesign = (d) => ({ ...d, lines: d.lines.filter((l) => !l.pristine && l.text.trim()) });

export const refKey = (r) => (r ? `${r.kind}-${r.i}` : "");
